/**
 * Bernard Miniatures — read more / read less.
 *
 * The collapsed state is a CSS line clamp, so the full description is always in
 * the DOM. This script only decides whether a toggle is warranted, and animates
 * the height between the two states.
 *
 * The `is-expanded` class is the single source of truth; a settle timer always
 * clears `is-animating`, so a missed transitionend cannot strand the component
 * with its clamp lifted.
 */

const DEFAULT_SPEED = 320;
const SETTLE_MARGIN = 60;

class BmReadMore extends HTMLElement {
  #frame = null;
  #settleTimer = null;

  connectedCallback() {
    this.body = this.querySelector('[data-bm-rm-body]');
    this.content = this.querySelector('[data-bm-rm-content]');
    this.toggle = this.querySelector('[data-bm-rm-toggle]');
    this.label = this.querySelector('[data-bm-rm-label]');
    if (!this.body || !this.content || !this.toggle) return;

    this.moreLabel = this.querySelector('[data-bm-rm-more]')?.textContent.trim() || 'Lire la suite';
    this.lessLabel = this.querySelector('[data-bm-rm-less]')?.textContent.trim() || 'Réduire';
    this.speed = Number(this.dataset.speed);
    if (!Number.isFinite(this.speed)) this.speed = DEFAULT_SPEED;
    this.style.setProperty('--bm-rm-speed', `${this.speed}ms`);

    this.toggle.addEventListener('click', this.#onToggle);

    this.#evaluate();

    // Re-check when the text reflows: a narrower column needs more lines, so a
    // description that fitted at desktop width may overflow on a phone.
    // Deferred to a frame so the measuring does not run inside the observer
    // callback, which browsers flag as a resize loop.
    this.resizeObserver = new ResizeObserver(() => this.#scheduleEvaluate());
    this.resizeObserver.observe(this);

    // A hidden tab runs no frames, so anything deferred above is still pending
    // when it comes back. Re-check on the way in.
    document.addEventListener('visibilitychange', this.#onVisibility);

    // Web fonts land after first paint and change the line height.
    document.fonts?.ready.then(() => this.refresh());
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#settleTimer);
  }

  /**
   * Re-measure the description and show or hide the toggle accordingly.
   * Public so anything that injects content into the description — a
   * translation app, a tab switch — can ask for a fresh read.
   */
  refresh() {
    this.#evaluate();
  }

  #onVisibility = () => {
    if (document.visibilityState === 'visible') this.#scheduleEvaluate();
  };

  #scheduleEvaluate() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#evaluate());
  }

  get #expanded() {
    return this.classList.contains('is-expanded');
  }

  get #busy() {
    return this.classList.contains('is-animating');
  }

  /** Height of the body with the clamp applied. */
  #clampedHeight() {
    const wasShort = this.classList.contains('is-short');
    this.classList.remove('is-short');
    this.body.style.maxHeight = 'none';
    const height = this.content.getBoundingClientRect().height;
    if (wasShort) this.classList.add('is-short');
    return height;
  }

  /** Height the body would take with the clamp lifted. */
  #fullHeight() {
    this.classList.add('is-short');
    this.body.style.maxHeight = 'none';
    const height = this.content.getBoundingClientRect().height;
    this.classList.remove('is-short');
    return height;
  }

  /**
   * Decide whether the description is long enough to warrant a toggle, and
   * park the body at its clamped height.
   */
  #evaluate() {
    if (this.#expanded || this.#busy) return;

    const clamped = this.#clampedHeight();
    const full = this.#fullHeight();
    const overflows = full - clamped > 4;

    this.toggle.hidden = !overflows;
    this.classList.toggle('is-short', !overflows);
    this.body.style.maxHeight = overflows ? `${clamped}px` : '';
  }

  #onToggle = () => {
    const willExpand = !this.#expanded;
    const from = this.body.getBoundingClientRect().height;

    this.toggle.setAttribute('aria-expanded', String(willExpand));
    if (this.label) this.label.textContent = willExpand ? this.lessLabel : this.moreLabel;

    // `is-animating` lifts the clamp for the whole transition, so the text does
    // not reflow halfway through in either direction.
    this.classList.add('is-animating');
    this.classList.toggle('is-expanded', willExpand);

    const to = willExpand ? this.#fullHeight() : this.#clampedHeight();

    this.body.style.maxHeight = `${from}px`;
    // Force a reflow so the transition runs from the start value.
    void this.body.offsetHeight;
    this.body.style.maxHeight = `${to}px`;

    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => this.#settle(), this.speed + SETTLE_MARGIN);
  };

  #settle() {
    this.classList.remove('is-animating');
    if (this.#expanded) {
      // Let the text grow freely once open, so a later reflow cannot clip it.
      this.body.style.maxHeight = 'none';
      return;
    }
    // Re-measure with the clamp back on: the pixel value keeps overflow hidden
    // without depending on the clamp alone.
    this.body.style.maxHeight = '';
    void this.body.offsetHeight;
    this.body.style.maxHeight = `${this.content.getBoundingClientRect().height}px`;
  }
}

if (!customElements.get('bm-readmore')) {
  customElements.define('bm-readmore', BmReadMore);
}
